import type { StufenBeschreibung } from '@luzula/luzula-lib';

/**
 * Karbonatgehaltsstufen nach SEA 95, Teil B, Darst. B 3-18 („Gehalt an
 * karbonatischer Substanz in Masse-%", S. 76).
 *
 * Bezugsbasis: **Masse-%** wie beim Humusgehalt — nicht Volumen-% wie beim
 * Skelettgehalt.
 *
 * Der Eingabewert ist die Ziffer des Kurzzeichens: 0 = k0 … 6 = k6. Die
 * Auswahlliste des Feldes führt genau diese sieben Werte.
 */
export interface KarbonatStufe {
	/** Eingabewert in der Substratfolge (0 … 6). */
	wert: number;
	/** Kurzzeichen nach SEA. */
	code: string;
	/** Spanne in Masse-%. */
	spanne: string;
	bezeichnung: string;
	/** Zweite Bezeichnung derselben Stufe (nur k5 und k6 führen eine). */
	auch?: string;
	/**
	 * Reaktion mit Salzsäure. Für k4 bis k6 nennt die Anweisung keine — dort
	 * bleibt das Feld leer, statt etwas zu erfinden.
	 */
	reaktion?: string;
}

export const KARBONAT_STUFEN: KarbonatStufe[] = [
	{ wert: 0, code: 'k0', spanne: '≤ 0,3 %', bezeichnung: 'karbonatfrei', reaktion: 'keine Reaktion' },
	{ wert: 1, code: 'k1', spanne: '0,3 … 2 %', bezeichnung: 'schwach karbonathaltig', reaktion: 'schwach aufbrausend' },
	{ wert: 2, code: 'k2', spanne: '2 … 5 %', bezeichnung: 'mäßig karbonathaltig', reaktion: 'stark, aber nicht anhaltend aufbrausend' },
	{ wert: 3, code: 'k3', spanne: '5 … 15 %', bezeichnung: 'stark karbonathaltig', reaktion: 'stark und anhaltend aufbrausend' },
	{ wert: 4, code: 'k4', spanne: '15 … 30 %', bezeichnung: 'sehr stark karbonathaltig' },
	{ wert: 5, code: 'k5', spanne: '30 … 70 %', bezeichnung: 'silikathaltiger Karbonatboden', auch: 'Halbkalk' },
	{ wert: 6, code: 'k6', spanne: '> 70 %', bezeichnung: 'Karbonatboden', auch: 'Kalk' },
];

/**
 * Von farblos nach kalkweiß-blau, Eingabewert = Index — nur zur Orientierung.
 * Färbt das Hilfefenster (Legende) UND das Feld in der Substrattabelle; eine
 * Reihe, nicht zwei.
 */
export const KARBONAT_FARBEN: readonly string[] = [
	'#ffffff', '#f2f6f8', '#e4eef3', '#d2e4ed', '#bcd8e6', '#a3cade', '#86bad5',
];

/** Die Stufen für das Stufen-Feld (`wd-stufen-input`): Wert, Farbe, Name. */
export const KARBONAT_STUFEN_FELD: StufenBeschreibung[] = KARBONAT_STUFEN.map(s => ({
	wert: s.wert, farbe: KARBONAT_FARBEN[s.wert], name: s.bezeichnung,
}));
